import React, { useRef, useLayoutEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { Box } from "@mui/material";
import {
  MyHrContractCard,
  MyHrStickyPersonalDataHeader,
  MyHrUserTwoFactorCard,
} from "my-hr/components";
import { MyHrEditRoleForm, MyHrUserProfileForm } from "my-hr/forms";
import {
  useMyHrContractsByEmployee,
  useMyHrEmployee,
  useMyHrUserByEmployee,
} from "my-hr/hooks";
import { OcPageNameTitle, OcPageTransition } from "oc/components";
import { useOcClientState, useOcIsMobile } from "oc/hooks";
import { ocScrollTriggerState } from "states";

export const MyHRAdminUserPage = () => {
  const { employee } = useParams<{ employee: string }>();
  const isMobile = useOcIsMobile();
  const { t } = useTranslation();

  const { data: userData, refetch: refetchUserData } = useMyHrUserByEmployee({ employee });
  const { data: employeeData } = useMyHrEmployee({ employeeId: employee });
  const { data: contractsData } = useMyHrContractsByEmployee({ employee });

  const selectedContract = contractsData?.[0] ?? null;

  const pageRef = useRef<HTMLDivElement>(null);

  const [scrollTrigger, setScrollTrigger] = useRecoilState(ocScrollTriggerState);
  const [pageWidth, setPageWidth] = useOcClientState<number>("pageWidth", 0);

  const handleScroll = useCallback(() => {
    const triggerY = 100;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    setScrollTrigger(scrollTop >= triggerY ? "show" : "hide");
  }, [setScrollTrigger]);

  const handleResize = useCallback(() => {
    const width = pageRef.current?.clientWidth;
    if (width && width !== pageWidth) {
      setPageWidth(width);
    }
  }, [pageWidth, setPageWidth]);

  useLayoutEffect(() => {
    handleResize();
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [handleScroll, handleResize]);

  return (
    <OcPageTransition>
      <Box ref={pageRef}>
        <MyHrStickyPersonalDataHeader
          pageWidth={pageWidth}
          employee={employeeData}
          contract={selectedContract}
          trigger={scrollTrigger === "show" ? true : false}
        />
        <OcPageNameTitle
          title={t("MY_HR_OTHER_USER_PAGE_TITLE")}
          goBack={true}
        />
        <Box id="personal-data-page" display="flex" flexDirection="column">
          <MyHrContractCard
            employee={employeeData}
            contract={selectedContract}
          />
          <Box padding={isMobile ? "16px 0px" : "16px"}>
            <MyHrUserProfileForm />
          </Box>
          <Box>
            <MyHrUserTwoFactorCard
              padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}
              userData={userData}
              refetchUserData={refetchUserData}
              isMobile={isMobile}
            />
          </Box>
          {contractsData?.map((contract: any) => {
            return (
              <MyHrEditRoleForm
                key={`${contract?.code}-edit-form`}
                contract={contract}
                isMobile={isMobile}
              />
            );
          })}
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyHRAdminUserPage;
